import React, { useEffect, useId, useState } from 'react';
import { Dialog } from '../../common';

interface TerminateCoverageDialogProps {
  open: boolean;
  payerLabel: string;
  effectiveDate: string;
  /** Agency-local today ('YYYY-MM-DD'): the default end date */
  today: string;
  busy?: boolean;
  error?: string | null;
  onConfirm: (terminationDate: string) => void | Promise<void>;
  onCancel: () => void;
}

/**
 * End a coverage by setting its last covered date (both dates are inclusive). Coverage is never
 * deleted: the history stays for claims on earlier dates of service (REQ-INS-08).
 */
const TerminateCoverageDialog: React.FC<TerminateCoverageDialogProps> = ({
  open, payerLabel, effectiveDate, today, busy = false, error, onConfirm, onCancel
}) => {
  const id = useId();
  const [date, setDate] = useState(today);

  useEffect(() => {
    if (open) setDate(today < effectiveDate ? effectiveDate : today);
  }, [open, today, effectiveDate]);

  const invalid = !date || date < effectiveDate;

  return (
    <Dialog open={open} onClose={() => !busy && onCancel()} labelledBy={`${id}-title`} describedBy={`${id}-desc`}>
      <form
        className="text-left"
        onSubmit={(e) => { e.preventDefault(); if (!invalid && !busy) void onConfirm(date); }}
      >
        <h3 id={`${id}-title`} className="text-lg font-semibold text-slate-800 pr-6">Terminate coverage</h3>
        <p id={`${id}-desc`} className="mt-1 text-sm text-slate-600">
          {payerLabel}. Visits after the last covered date are no longer billed to this coverage. The record is kept.
        </p>
        <label className="block mt-4 text-sm font-medium text-slate-700">
          Last covered date <span className="text-red-600" aria-hidden="true">*</span>
          <input
            type="date"
            value={date}
            min={effectiveDate}
            onChange={(e) => setDate(e.target.value)}
            disabled={busy}
            required
            aria-invalid={invalid}
            className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </label>
        {invalid && <p className="mt-1 text-xs text-red-700">Must be on or after the effective date ({effectiveDate}).</p>}
        {error && <p role="alert" className="mt-2 text-sm text-red-700">{error}</p>}
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onCancel} disabled={busy}
            className="px-4 py-2 rounded-md text-sm border border-slate-300 text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-60">
            Cancel
          </button>
          <button type="submit" disabled={invalid || busy}
            className="px-4 py-2 rounded-md text-sm bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed">
            {busy ? 'Working...' : 'Terminate'}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

export default TerminateCoverageDialog;
